/**
 * PORTED — RO-3-00f / #44473.
 * Origin: `excellent/app/agents/workflows/_fleet-console.tsx`.
 *
 * Cause D: the Next server component called `listFleetLoops` + `portfolioSummary`
 * against the vault. That read already lives on `workflowFleet.list` (RO-3-02b).
 * This is the shared client body so section mounts import once from
 * `client/shared` instead of inlining a second copy (as `agent-workflows.tsx`
 * previously did).
 */

import {
  Button,
  FleetTable,
  RunnabilityKpi,
  StatStrip,
  portfolioStatItems,
} from "@excellent/ui";
import { Plus } from "@excellent/ui/icons";
import { useApi } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import { workflowFleet } from "@excellent/ui/api/contracts/workflows/fleet";
import { WORKFLOW_RANGES, type WorkflowRange } from "@/optimization/db-ranges";

export function FleetConsole({ range = "all" }: { range?: WorkflowRange } = {}) {
  const { data, error, isLoading } = useApi(workflowFleet.list, {
    query: { range },
  });

  return (
    <div className="@container/page flex flex-col gap-5" data-section-loops="operations">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="type-body text-muted-foreground">
          Fleet console — supervise every optimization workflow by exception.
          {data && data.summary.blocked_loops ? (
            <>
              {" "}
              ·{" "}
              <Link
                href="/"
                className="text-foreground underline-offset-2 hover:underline"
              >
                {data.summary.blocked_loops} pending roles
              </Link>
            </>
          ) : null}
        </p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            {WORKFLOW_RANGES.map((r) => (
              <Button
                key={r.value}
                asChild
                size="sm"
                variant={r.value === range ? "default" : "outline"}
              >
                <Link href={`/?range=${r.value}`}>{r.label}</Link>
              </Button>
            ))}
          </div>
          <Button asChild size="icon-tb">
            <Link
              href="/workflows/new"
              aria-label="New workflow"
              title="New workflow"
            >
              <Plus className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
      <RunnabilityKpi />
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : isLoading || !data ? (
        <p className="type-body text-muted-foreground">Loading the fleet…</p>
      ) : (
        <FleetTable
          rows={data.rows}
          rangeLabel={data.rangeLabel}
          summary={
            <StatStrip
              variant="table"
              items={portfolioStatItems(data.summary, data.rangeLabel)}
            />
          }
        />
      )}
    </div>
  );
}
